import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuTrash } from "react-icons/lu";
import coursesInitial from "../courses";

const Course = ({ title, name, pholder, val, grade, marks, act, onDelete }) => {
  const idBase = title.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="course-card">
      <div className="course-header">
        <span className="badge">{title}</span>
        <button type="button" className="icon-btn" onClick={onDelete} aria-label="Remove course">
          <LuTrash className="icon" />
        </button>
      </div>

      <div className="fields">
        <div className="field">
          <label htmlFor={`${idBase}-credits`}>Credits</label>
          <input
            id={`${idBase}-credits`}
            type="number"
            name="val"
            placeholder={pholder}
            value={val}
            onChange={act}
            min="0"
          />
        </div>

        <div className="field">
          <label htmlFor={`${idBase}-marks`}>Total Weightage Marks</label>
          <input
            id={`${idBase}-marks`}
            type="number"
            name="marks"
            placeholder="0 - 100"
            value={marks}
            onChange={act}
            min="0"
            max="100"
          />
          <div className="inline-hint">Grades populate automatically from marks.</div>
        </div>

        <div className="field">
          <label htmlFor={`${idBase}-grade`}>Predicted Grade</label>
          <select id={`${idBase}-grade`} name="grade" value={grade} disabled>
            <option value="">Select Grade</option>
            <option value="10">S Grade</option>
            <option value="9">A Grade</option>
            <option value="8">B Grade</option>
            <option value="7">C Grade</option>
            <option value="6">D Grade</option>
            <option value="5">E Grade</option>
            <option value="0">F Grade</option>
          </select>
        </div>
      </div>
    </div>
  );
};

const GradePredict = () => {
  const [courses, setCourses] = useState([]);
  const [gpa, setGpa] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [resultTone, setResultTone] = useState("success");
  const [resultNote, setResultNote] = useState("");

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedCourses = JSON.parse(localStorage.getItem("courses"));
    if (savedCourses) setCourses(savedCourses);
    else setCourses(coursesInitial);
  }, []);

  // Save data to localStorage explicitly when "Save" is clicked
  const handleSave = () => {
    localStorage.setItem("courses", JSON.stringify(courses));
    setFeedback({ type: "success", message: "Courses saved locally for quick reuse." });
  };

  // Handle input changes
  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    setCourses((prevCourses) => {
      const updatedCourses = [...prevCourses];
      updatedCourses[index][name] = value;

      // Calculate grade based on marks
      if (name === "marks") {
        const marks = parseFloat(value) || 0;
        let grade;
        if (marks >= 90) grade = "10";
        else if (marks >= 80) grade = "9";
        else if (marks >= 70) grade = "8";
        else if (marks >= 60) grade = "7";
        else if (marks >= 55) grade = "6";
        else if (marks >= 50) grade = "5";
        else grade = "0";
        updatedCourses[index]["grade"] = grade;
      }
      return updatedCourses;
    });
  };

  // Add a new course
  const handleAddCourse = () => {
    setCourses((prevCourses) => [
      ...prevCourses,
      {
        key: prevCourses.length + 1,
        title: `Course ${prevCourses.length + 1}`,
        name: "val",
        pholder: "Credits",
        val: "",
        marks: "",
        grade: "",
      },
    ]);
  };

  // Delete a course
  const handleDeleteCourse = (index) => {
    setCourses((prevCourses) => prevCourses.filter((_, i) => i !== index));
  };

  // Reset input fields (but keep props intact)
  const handleReset = () => {
    setCourses((prevCourses) =>
      prevCourses.map((course) => ({
        ...course,
        val: "",
        marks: "",
        grade: "",
      }))
    );
  };

  // Calculate GPA
  const handleCalculateGPA = () => {
    setIsCalculating(true);
    const totalCredits = courses.reduce(
      (sum, course) => sum + (parseFloat(course.val) || 0),
      0
    );
    const weightedGrades = courses.reduce(
      (sum, course) =>
        sum + (parseFloat(course.val) || 0) * (parseFloat(course.grade) || 0),
      0
    );

    if (totalCredits === 0) {
      setGpa(0);
      setResultTone("warning");
      setResultNote("Add credits and marks to see a realistic GPA prediction.");
    } else {
      const calculated = (weightedGrades / totalCredits).toFixed(2);
      setGpa(calculated);

      const value = parseFloat(calculated);
      if (value >= 9) {
        setResultTone("success");
        setResultNote("Sweet spot - strong for dream/TT drives. Keep internals tidy.");
      } else if (value >= 8) {
        setResultTone("warning");
        setResultNote("Nice - push labs/theory to nudge into 9 club.");
      } else if (value >= 7) {
        setResultTone("warning");
        setResultNote("Borderline comfort - tighten internals to avoid surprise drops.");
      } else {
        setResultTone("danger");
        setResultNote("At risk - aim for high grades in heavy-credit slots to recover.");
      }
    }

    setTimeout(() => setIsCalculating(false), 160);
  };

  const hasInvalidInputs = courses.some(
    (course) =>
      (course.val && parseFloat(course.val) < 0) ||
      (course.marks && parseFloat(course.marks) < 0) ||
      (course.marks && parseFloat(course.marks) > 100)
  );

  const primaryLabel = isCalculating ? "Calculating..." : "Calculate GPA";

  return (
    <div className="app-shell">
      <main className="page">
        <div className="nav-actions">
          <Link to="/" className="btn btn-ghost">← Home</Link>
          <span className="pill">Grade Predictor</span>
        </div>

        <header className="page-header">
          <p className="eyebrow">Estimator</p>
          <h1 className="page-title">Predict grades from marks</h1>
          <p className="lede">Approximate your GPA using absolute grading. Relative adjustments may vary.</p>
        </header>

        <div className="card">
          <div className="card-heading">
            <div>
              <h2 className="card-title">Courses</h2>
              <p className="section-note">Marks convert to grades automatically; credits weight your GPA.</p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleAddCourse}>
              + Add course
            </button>
          </div>

          <div className="stack">
            {courses.map((course, index) => (
              <Course
                key={index}
                title={course.title}
                name="val"
                pholder={course.pholder}
                val={course.val}
                marks={course.marks}
                act={(e) => handleInputChange(index, e)}
                grade={course.grade}
                onDelete={() => handleDeleteCourse(index)}
              />
            ))}
          </div>

          <div className="controls">
            <button type="button" className="btn btn-secondary" onClick={handleSave}>
              Save
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCalculateGPA}
              disabled={hasInvalidInputs || isCalculating}
            >
              {primaryLabel}
            </button>
          </div>
          <div className="inline-hint">Grades use an absolute scale; outputs are indicative.</div>
          {feedback && <div className={`feedback ${feedback.type}`} aria-live="polite">{feedback.message}</div>}
        </div>

        {gpa !== null && (
          <div className={`result-card ${resultTone}`}>
            <p className="result-label">Approximate GPA</p>
            <p className="result-value">{gpa}</p>
            <p className="muted">Calculated with provided credits and grades derived from marks.</p>
            {resultNote && <p className="muted" style={{ marginTop: 6 }}>{resultNote}</p>}
          </div>
        )}
      </main>
    </div>
  );
};

export default GradePredict;
